import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useProducts } from '../contexts/ProductsContext';
import { useSettings } from '../contexts/SettingsContext';
import { cloudinaryUrl, cloudinarySrcSet } from '../lib/cloudinary';
import ProductCard from '../components/ProductCard';

const BASE = process.env.REACT_APP_BACKEND_URL;
function resolveUrl(u) { return (!u || u.startsWith('http')) ? u : `${BASE}${u}`; }

const COLLECTION_META = {
  'gold-edit':   { title: 'The Gold Edit',   subtitle: 'Bold, sculptural, unforgettable.', eyebrow: 'Editorial' },
  'pearl-edit':  { title: 'The Pearl Edit',  subtitle: 'Timeless elegance with luminous pearls.', eyebrow: 'Editorial' },
  'after-hours': { title: 'After Hours',     subtitle: 'Statement pieces for unforgettable evenings.', eyebrow: 'Evening' },
};

export default function CollectionPage() {
  const { slug } = useParams();
  const { products, collections } = useProducts();
  const { settings } = useSettings();
  const [sort, setSort] = useState('newest');

  // Instant — zero API call
  const items = useMemo(() => {
    let list = products.filter(p => p.collection === slug && p.is_visible !== false);
    switch (sort) {
      case 'price_asc':  return [...list].sort((a, b) => a.price - b.price);
      case 'price_desc': return [...list].sort((a, b) => b.price - a.price);
      case 'featured':   return [...list].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
      default:           return [...list].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }
  }, [products, slug, sort]);

  const meta    = COLLECTION_META[slug] || { title: slug, subtitle: '', eyebrow: 'Collection' };
  const col     = collections.find(c => c.slug === slug);
  const customImg = settings?.collection_images?.[slug];
  const bannerImg = resolveUrl(customImg) || resolveUrl(col?.image_url) || '';

  return (
    <main data-testid="collection-page" className="pt-20 min-h-screen bg-[#F6F3EE]">

      {/* Banner */}
      <div className="relative h-56 md:h-80 lg:h-96 overflow-hidden bg-[#E8E1D8]">
        {bannerImg && (
          <img
            src={cloudinaryUrl(bannerImg, { width: 1200, quality: 'eco' })}
            srcSet={cloudinarySrcSet(bannerImg, [600, 900, 1400])}
            sizes="100vw"
            alt={meta.title}
            className="w-full h-full object-cover"
            fetchpriority="high"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1C1A]/70 via-[#1E1C1A]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-24">
          <p className="text-[9px] md:text-[10px] font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-1.5">{meta.eyebrow}</p>
          <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl font-light text-[#F6F3EE] tracking-tight mb-1">{meta.title}</h1>
          <p className="text-xs md:text-sm font-light text-[#F6F3EE]/70">{meta.subtitle}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 py-5 md:py-8 flex items-center justify-between border-b border-[#DDD7CE]">
        <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7369]">{items.length} piece{items.length !== 1 ? 's' : ''}</p>
        <select value={sort} onChange={e => setSort(e.target.value)}
          className="appearance-none bg-transparent border border-[#DDD7CE] text-[10px] uppercase tracking-[0.12em] text-[#7A7369] px-3 py-1.5 cursor-pointer outline-none">
          <option value="newest">Newest</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
          <option value="featured">Featured First</option>
        </select>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 py-8 md:py-12">
        {items.length === 0 ? (
          <div className="text-center py-24">
            <p className="font-['Cormorant_Garamond'] text-2xl font-light text-[#3A3632] mb-3">No pieces in this collection yet.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#7A7369] border-b border-[#DDD7CE] pb-0.5 hover:text-[#C6A978] hover:border-[#C6A978] transition-colors">
              Browse all pieces <ArrowRight size={10} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
            {items.map((product, i) => (
              <ProductCard
                key={product.id}
                product={product}
                index={i}
                thumbWidth={480}
                sizes="(max-width:768px) 50vw, (max-width:1200px) 33vw, 25vw"
                data-testid={`collection-product-${i}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Related collections */}
      <div className="border-t border-[#DDD7CE] py-10 md:py-16 px-4 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-[#C6A978] mb-6 text-center">More Collections</p>
          <div className="flex flex-wrap justify-center gap-3">
            {Object.entries(COLLECTION_META).filter(([s]) => s !== slug).map(([s, m]) => (
              <Link key={s} to={`/collection/${s}`}
                className="px-5 py-2 border border-[#DDD7CE] text-[10px] uppercase tracking-[0.15em] text-[#7A7369] hover:border-[#C6A978] hover:text-[#3A3632] transition-colors">
                {m.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}